import { IconButtonProps } from '@mui/material';
import { captureException } from '@sentry/react';
import { useMemo } from 'react';

import { FavoriteSiteActionIcon } from '@frontend-libs/portal';

import { preferenceClient } from '@portal-clients/preference';

import { useUser } from '@frontend/utils';

export interface FavouriteSiteActionProps {
  siteUnitId: string;
  size?: IconButtonProps['size'];
  bg?: string;
}

export const FavoriteSiteAction = (props: FavouriteSiteActionProps) => {
  const { user, updatePreferences } = useUser();

  const favourites = useMemo(() => {
    if (user?.preferences?.sites === undefined) {
      return [];
    }

    return user.preferences.sites;
  }, [user?.preferences?.sites]);

  const handleFavouriteClick = (id: string, favourite: boolean) => {
    if (!user || !id) return;

    if (favourite) {
      addFavourite(id).catch((error) => {
        console.error(error);
        captureException(error);
      });
    } else {
      removeFromFavourites(id).catch((error) => {
        console.error(error);
        captureException(error);
      });
    }
  };

  const removeFromFavourites = async (id: string) => {
    if (!favourites.includes(id)) {
      return;
    }

    const remainingIds = favourites.filter((site) => site !== id);

    updatePreferences({
      sites: remainingIds,
    });

    await preferenceClient
      .set('sites', {
        preference: {
          ids: remainingIds,
        },
      })
      .catch((e) => {
        captureException(e, {
          level: 'warning',
          extra: {
            message:
              'Failed to update preferences to remove site from favourites',
          },
        });
      });
  };

  const addFavourite = async (id: string) => {
    if (favourites.includes(id)) {
      return;
    }

    const ids = [...favourites, id];

    updatePreferences({
      sites: ids,
    });

    await preferenceClient
      .set('sites', {
        preference: {
          ids: ids,
        },
      })
      .catch((e) => {
        captureException(e, {
          level: 'warning',
          extra: {
            message: 'Failed to update preferences to add site to favourites',
          },
        });
      });
  };

  return (
    <FavoriteSiteActionIcon
      siteUnitId={props.siteUnitId}
      size={props.size}
      bg={props.bg}
      isFavorite={favourites.includes(props.siteUnitId)}
      handleFavouriteClick={handleFavouriteClick}
    />
  );
};
